/**
 * Build Store
 *
 * Manages build status, deployment configuration, and preview state.
 *
 * E.21.30 (2026-05-04) wired the previously-stub store to the backend:
 *   - startBuild(projectId)        POST /api/builds/{id}
 *   - pollBuildStatus(projectId)   GET /api/builds/{id}/status @ 1Hz
 *   - parseLogsForProblems(text)   tsc/Vite regex → BuildError[]
 *
 * Polling chosen over SSE per plan v2 reviewer C1: api.ts stream() helper is
 * hardcoded POST, and the backend stream endpoint is GET. Polling closes the
 * gate without adding a new helper. Builds finish in seconds-minutes so 1Hz
 * polling is bounded.
 */
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { api, ApiError } from '@/services/api';
import type {
  BuildStatus,
  BuildStage,
  BuildError,
} from '@/types';

interface BuildState {
  // Build status
  buildStatus: BuildStatus;

  // Preview
  previewUrl: string | null;
  previewLoading: boolean;
  // 2026-06-27 — which file the preview is showing. '' = Built app
  // (dist/index.html); a rel path like 'ornith/or-shooter.html' = that file.
  // null = nothing selected yet. Drives the PreviewPanel file dropdown value.
  previewPath: string | null;

  // Build logs
  buildLogs: string[];

  // Actions - Build
  setBuildStatus: (status: BuildStatus['status']) => void;
  setBuildComplete: (
    success: boolean,
    durationMs: number,
    errors?: BuildError[],
    warnings?: BuildError[]
  ) => void;
  addBuildLog: (log: string) => void;
  clearBuildLogs: () => void;

  // Actions - Preview
  setPreviewUrl: (url: string | null) => void;
  setPreviewLoading: (loading: boolean) => void;
  refreshPreview: () => void;
  // 2026-06-27 — point the preview iframe at a specific file (or the Built
  // app when relPath is '' / 'index.html'). Backend serves dist-first then
  // the project source root, so standalone .html demos render without a build.
  previewFile: (projectId: string, relPath: string) => void;

  // E.21.30 — backend-wired build lifecycle. Gap A1 (2026-08-18): optional
  // mode selects the backend build script — 'production' runs build:public
  // (the deployable artifact); omitted = preview build (unchanged default).
  startBuild: (projectId: string, mode?: 'development' | 'production') => Promise<void>;
  pollBuildStatus: (projectId: string) => Promise<void>;

  // E.21.31 — preview iframe via static dist/ serving
  previewProject: (projectId: string | undefined) => Promise<void>;

  // E.21.34 — user-initiated cancel
  cancelBuild: (projectId: string) => Promise<void>;
}

interface BuildStatusResponse {
  projectId: string;
  status:
    | 'none'
    | 'pending'
    | 'installing'
    | 'building'
    | 'deploying'
    | 'success'
    | 'error'
    | 'cancelled'; // E.21.34 typed cancel status
  startedAt?: string | null;
  completedAt?: string | null;
  durationMs?: number | null;
  previewUrl?: string | null;
  error?: string | null;
  // True when the build pipeline auto-recovered a corrupt/missing native dep.
  healed?: boolean | null;
  // P4: which npm script ran, and whether a `build:public` exists. Reported by
  // the backend (it reads package.json and chooses); never inferred here.
  buildScript?: string | null;
  publicBuildAvailable?: boolean | null;
}

/**
 * E.21.30 — parse tsc / Vite build output into structured BuildError[].
 *
 * Recognized format (tsc default):
 *   src/App.tsx(5,8): error TS2307: Cannot find module '@/utils/format'.
 *   src/App.tsx(12,3): warning TS6133: 'unused' is declared but never read.
 *
 * Reviewer N7 fallback: when 0 regex matches AND isError=true, synthesize
 * one BuildError carrying the raw text (truncated). This handles the
 * `npm install` failure case where backend dumps stdout into result.error
 * without tsc-style line/column info — without it, a failed install would
 * show "0 errors" while the build clearly failed.
 */
export function parseLogsForProblems(
  input: string,
  options: { isError?: boolean } = {},
): BuildError[] {
  if (!input) return [];

  const TSC_PATTERN = /^(.+?)\((\d+),(\d+)\):\s+(error|warning)\s+\w+:\s+(.+)$/gm;
  const errors: BuildError[] = [];
  let match: RegExpExecArray | null;
  while ((match = TSC_PATTERN.exec(input)) !== null) {
    errors.push({
      file: match[1].trim(),
      line: parseInt(match[2], 10),
      column: parseInt(match[3], 10),
      severity: match[4] as 'error' | 'warning',
      message: match[5].trim(),
    });
  }

  if (errors.length === 0 && options.isError) {
    // No structured matches but the build failed — synthesize a single
    // error so the user sees something. Truncate to keep the panel usable.
    const MAX = 500;
    const truncated = input.length > MAX ? input.slice(0, MAX) + '…' : input;
    return [
      {
        file: '<build>',
        line: 0,
        column: 0,
        severity: 'error',
        message: truncated,
      },
    ];
  }

  return errors;
}

const DEFAULT_BUILD_STATUS: BuildStatus = {
  status: 'idle',
  stage: null,
  lastBuildAt: null,
  buildDurationMs: null,
  errors: null,
  warnings: null,
  healed: false,
  buildScript: null,
  publicBuildAvailable: false,
};

export const useBuildStore = create<BuildState>()(
  immer((set, get) => ({
    // Initial state
    buildStatus: DEFAULT_BUILD_STATUS,
    previewUrl: null,
    previewLoading: false,
    previewPath: null,
    buildLogs: [],

    // Build actions
    setBuildStatus: (status) =>
      set((state) => {
        state.buildStatus.status = status;
        if (status === 'building') {
          state.buildLogs = [];
        }
      }),

    setBuildComplete: (success, durationMs, errors, warnings) =>
      set((state) => {
        state.buildStatus.status = success ? 'success' : 'error';
        state.buildStatus.lastBuildAt = new Date().toISOString();
        state.buildStatus.buildDurationMs = durationMs;
        state.buildStatus.errors = errors ?? null;
        state.buildStatus.warnings = warnings ?? null;
      }),

    addBuildLog: (log) =>
      set((state) => {
        state.buildLogs.push(log);
        // Keep only last 500 lines
        if (state.buildLogs.length > 500) {
          state.buildLogs = state.buildLogs.slice(-500);
        }
      }),

    clearBuildLogs: () =>
      set((state) => {
        state.buildLogs = [];
      }),

    // Preview actions
    setPreviewUrl: (url) =>
      set((state) => {
        state.previewUrl = url;
      }),

    setPreviewLoading: (loading) =>
      set((state) => {
        state.previewLoading = loading;
      }),

    refreshPreview: () => {
      const url = get().previewUrl;
      if (url) {
        // Force refresh by appending timestamp
        const refreshUrl = url.includes('?')
          ? `${url}&_t=${Date.now()}`
          : `${url}?_t=${Date.now()}`;
        set((state) => {
          state.previewUrl = refreshUrl;
          state.previewLoading = true;
        });
      }
    },

    previewFile: (projectId, relPath) => {
      const clean = relPath.replace(/^\/+/, '');
      // '' or index.html → the Built app: use the trailing-slash root URL so
      // a built React app's relative asset paths (./assets/index.js) resolve.
      const isBuiltApp = clean === '' || clean === 'index.html';
      set((state) => {
        state.previewUrl = isBuiltApp
          ? `/api/preview/${projectId}/`
          : `/api/preview/${projectId}/${clean}`;
        state.previewPath = isBuiltApp ? '' : clean;
        state.previewLoading = true;
      });
    },

    startBuild: async (projectId, mode) => {
      set((state) => {
        state.buildStatus.status = 'building';
        state.buildStatus.stage = 'pending';
        state.buildStatus.errors = null;
        state.buildStatus.warnings = null;
        state.buildStatus.healed = false;
        state.buildStatus.buildScript = null;
        state.buildStatus.publicBuildAvailable = false;
        state.buildLogs = [];
        state.previewUrl = null;
      });
      try {
        await api.post(`/builds/${projectId}`, { mode: mode ?? 'development' });
        // Caller is responsible for invoking pollBuildStatus(projectId) after
        // startBuild resolves; we keep them separate so the UI can choose to
        // fire-and-forget or await.
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : String(err);
        set((state) => {
          state.buildStatus.status = 'error';
          state.buildStatus.stage = 'error';
          state.buildStatus.errors = [
            {
              file: '<build>',
              line: 0,
              column: 0,
              severity: 'error',
              message: `Build start failed: ${msg}`,
            },
          ];
        });
      }
    },

    pollBuildStatus: async (projectId) => {
      // E.21.31.2: each tick fetches BOTH /status and /logs in parallel so
      // the InfoPanel.OutputTab can render incremental log output during the
      // build. The backend's BuildResult.logs grows append-only; the frontend
      // replaces buildStore.buildLogs wholesale each tick (idempotent if
      // length unchanged). The `stage` field preserves the backend's
      // verbatim status enum for richer UX.
      //
      // Backend statuses: 'none' | 'pending' | 'installing' | 'building' |
      // 'deploying' | 'success' | 'error'. Frontend BuildStatusType only has
      // 'idle' | 'building' | 'success' | 'error' — installing/deploying
      // collapse to 'building' for the binary UI gate.
      const POLL_INTERVAL_MS = 1000;
      const MAX_POLLS = 600; // 10 minutes hard cap
      for (let i = 0; i < MAX_POLLS; i++) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        let resp: BuildStatusResponse;
        try {
          resp = await api.get<BuildStatusResponse>(`/builds/${projectId}/status`);
        } catch {
          // Transient network error during polling — keep trying. If the
          // backend is genuinely down, the user will see status stay at
          // 'building' until MAX_POLLS exhausts; that's acceptable v0.1.
          continue;
        }

        // Fetch logs in parallel-ish (don't block polling cadence on it).
        // Logs route may 404 if backend dropped the build record; swallow.
        try {
          const logsResp = await api.get<{ logs?: string[] }>(`/builds/${projectId}/logs`);
          if (Array.isArray(logsResp.logs)) {
            const fetched = logsResp.logs;
            set((state) => {
              if (fetched.length !== state.buildLogs.length) {
                state.buildLogs = fetched;
              }
            });
          }
        } catch {
          // No logs available yet (build may not have produced any output).
        }

        // Keep the verbatim backend stage updated each tick — UI uses it
        // for sub-stage labels ("Installing dependencies...", etc).
        set((state) => {
          state.buildStatus.stage =
            resp.status === 'none' ? null : (resp.status as BuildStage);
        });

        const isTerminal =
          resp.status === 'success' ||
          resp.status === 'error' ||
          resp.status === 'cancelled';
        const isProgress =
          resp.status === 'installing' ||
          resp.status === 'building' ||
          resp.status === 'deploying' ||
          resp.status === 'pending';

        if (isProgress) {
          continue;
        }

        if (isTerminal) {
          // Cancel maps to UI status='idle' (reset to pre-build), no
          // errors, one warning explaining the cancellation, no
          // previewUrl. The typed cancelled branch is checked before
          // the generic isError path so a user-cancel never gets
          // misclassified as a real failure.
          if (resp.status === 'cancelled') {
            set((state) => {
              state.buildStatus.status = 'idle';
              state.buildStatus.lastBuildAt = resp.completedAt ?? new Date().toISOString();
              state.buildStatus.buildDurationMs = resp.durationMs ?? null;
              state.buildStatus.errors = null;
              state.buildStatus.warnings = [
                {
                  file: '<build>',
                  line: 0,
                  column: 0,
                  severity: 'warning',
                  message: resp.error || 'Build cancelled by user',
                },
              ];
              // No previewUrl: user explicitly aborted, no auto-preview.
            });
            return;
          }

          const isError = resp.status === 'error';

          // E.21.36+1 (2026-05-05): the dead Step 3 (Docker packaging)
          // was removed from BuildService. Pre-removal, every build hit
          // status='error' even when Vite produced dist/, and this block
          // had a HEAD-and-reframe workaround. Post-removal, the backend
          // returns status='success' directly when Vite succeeds, so the
          // workaround is gone — `isError` now means a real Vite or
          // npm-install failure, not a phantom Docker step.
          const problems = parseLogsForProblems(resp.error ?? '', { isError });
          set((state) => {
            state.buildStatus.status = isError ? 'error' : 'success';
            state.buildStatus.lastBuildAt = resp.completedAt ?? new Date().toISOString();
            state.buildStatus.buildDurationMs = resp.durationMs ?? null;
            state.buildStatus.healed = resp.healed ?? false;
            state.buildStatus.buildScript = resp.buildScript ?? null;
            state.buildStatus.publicBuildAvailable = resp.publicBuildAvailable ?? false;
            state.buildStatus.errors = isError ? problems : null;
            state.buildStatus.warnings = isError
              ? null
              : problems.filter((p) => p.severity === 'warning');
            // On success, point the preview iframe at the canonical
            // same-origin static-serve route, CACHE-BUSTED. The built-app
            // URL is otherwise identical build-to-build, so the browser
            // keeps serving the previously-compiled modules cached at the
            // same URL and a rebuild is invisible until a manual refresh
            // (the stale-bundle trap the Pi refresh_preview tool worked
            // around — this is the root fix). The `?_t=` forces the iframe
            // to reload the fresh bundle; previewLoading shows the spinner
            // until the iframe's onLoad clears it (same as refreshPreview).
            if (!isError) {
              state.previewUrl = `/api/preview/${projectId}/?_t=${Date.now()}`;
              state.previewLoading = true;
            }
          });
          return;
        }

        // status === 'none' — backend lost the build record (e.g. service
        // restart). Treat as terminal failure so the user isn't stuck.
        set((state) => {
          state.buildStatus.status = 'error';
          state.buildStatus.stage = 'error';
          state.buildStatus.errors = [
            {
              file: '<build>',
              line: 0,
              column: 0,
              severity: 'error',
              message: 'Build status lost on backend (service restart?)',
            },
          ];
        });
        return;
      }

      // Hard cap reached — surface as failure rather than infinite spinner.
      set((state) => {
        state.buildStatus.status = 'error';
        state.buildStatus.stage = 'error';
        state.buildStatus.errors = [
          {
            file: '<build>',
            line: 0,
            column: 0,
            severity: 'error',
            message: 'Build timed out after 10 minutes',
          },
        ];
      });
    },

    previewProject: async (projectId) => {
      // Reviewer C3: PreviewPanel guards null currentProject by passing
      // currentProject?.id which can be undefined. No-op cleanly.
      if (!projectId) return;

      // Reviewer N4: HEAD via bare fetch (template literal — bypasses
      // the no-restricted-syntax rule which only fires on Literal type).
      // api.get would call response.json() on the HTML body and throw
      // a parse error even on HTTP 200, breaking the feature on success.
      // previewProject is a user-triggered action that queries the
      // backend route's filesystem state directly — it does NOT gate on
      // the in-memory buildStatus, so a stale buildStatus from a prior
      // session or refresh doesn't block the user from previewing a
      // valid dist/. The backend route returns 200 iff dist/index.html
      // exists on disk; that's the only signal that matters here.
      const url = `/api/preview/${projectId}/index.html`;
      let resp: Response;
      try {
        resp = await fetch(url, { method: 'HEAD' });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        set((state) => {
          state.buildStatus.errors = [
            {
              file: '<preview>',
              line: 0,
              column: 0,
              severity: 'error',
              message: `Preview unavailable: ${msg}`,
            },
          ];
        });
        return;
      }

      if (resp.ok) {
        set((state) => {
          state.previewUrl = `/api/preview/${projectId}/`;
          // Clear pre-existing build errors — preview succeeded means
          // dist/ is on disk regardless of what the build pipeline's
          // status said about the Docker step.
          state.buildStatus.errors = null;
        });
        return;
      }

      // 404 = no dist/ yet, prompt user to Build.
      set((state) => {
        state.buildStatus.errors = [
          {
            file: '<preview>',
            line: 0,
            column: 0,
            severity: 'error',
            message: 'Preview unavailable: no build artifacts. Click Build first to produce dist/.',
          },
        ];
      });
    },

    cancelBuild: async (projectId) => {
      // E.21.34: POST /api/builds/<id>/cancel. Backend's cancel_build
      // sets BuildStatus.CANCELLED (E.21.34 typed enum). The next
      // pollBuildStatus tick reads resp.status === 'cancelled' and
      // surfaces the cancel-warning Problem (handled in pollBuildStatus
      // BEFORE the distReady HEAD per reviewer N2).
      try {
        await api.post(`/builds/${projectId}/cancel`);
      } catch (err) {
        // Cancel failure (e.g. backend says no active build to cancel)
        // is non-fatal. Log so the user gets feedback if it happens.
        const msg = err instanceof ApiError ? err.message : String(err);
        console.warn('[buildStore] cancelBuild failed:', msg);
      }
    },
  }))
);
